import { IconButton, styled } from '@material-ui/core';
import { attributes, Collection, Linked, type } from '@type-r/models';
import { useIO, useLinked, useModel } from '@type-r/react';
import { asCurrency, asDate, Button, CollapsableSection, colors, div, Flex, FormCheckbox, Loading, PageHeaderTile, PageTileField, PopoverValue, PopupIODialog, PrimaryButton, SearchInput, SelectSortBy, TilesList, Toolbar } from 'lqx-react';
import * as React from 'react';
import { globalStore } from 'api';
import { restfulIO } from 'api/endpoint';
import { execSetEmailSentFlag, RecsInvoice } from 'src/app/pages/inmatch/api';
import { CustomerSupplier, PaymentInfoIcon, RowH1, RowH2Bold } from '../../../../../common';
import { InvoiceDetails, InvoiceTile } from '../../../../../common/invoice-aging';
import { root } from '../api/common';
import { CreateRADialog } from './createRADialog';
import { OpenInvoicesFilter } from '../matching/unreconciled/open-invoices';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs } from 'src/app/models/UserEntitlements';

const State = attributes({
    filter: '',
    sortBy: {
        name: 'invoiceNumber',
        isAsc: false
    },
    invoices: type([RecsInvoice])
        .dontSave
        .endpoint(restfulIO(root + 'invoice/filtered')),

    selected: Collection.ofRefs(RecsInvoice)
})

export const CustomerDebthList = ({ customerDebth, range, status, companies }) => {
    const state = useModel(State);

    const $doIO = useLinked(false);
    
    const isReady = useIO(async () => {
        if (customerDebth) {
            await state.invoices.fetch({
                params: {
                    range,
                    status,
                    customer: customerDebth.customer.id,
                    supplier: customerDebth.supplier.id
                }
            });

            state.selected.reset();

            state.$.filter.set('');
        }
        else {
            state.invoices.reset();
        }
    }, [$doIO.value, customerDebth])
    
    const sortedAndFiltered = React.useMemo(() => {
        const { name, isAsc } = state.sortBy;
        return state.invoices
            .filter(inv => inv.filter(new OpenInvoicesFilter({filterAll: state.filter})))
            .sort(RecsInvoice.comparator(name as any, isAsc));
    },
    [ state.invoices['_changeToken'], state.sortBy['_changeToken'], state.filter ]);

    React.useEffect(() => {
        if (state.filter) {
            state.selected.reset();
        }
    }, [state.filter])

    const mailTo = () => {
        const a = document.createElement('a');

        const relationship = globalStore.getRelationship(state.selected.first().conversationId);

        const email = {
            subject: `${customerDebth.supplier.name} ${relationship.Customer.externalRelationshipId} Promise to Pay date`,
            body:
                `Dear Customer,

Can you please review below invoice/invoices and provide your approval by providing promise to pay date.


{{ Please, paste invoices from the clipboard. }} 


Best Regards,
${customerDebth.supplier.name} 
AR team`,
        }

        const invoiceRows = state.selected
            .map(inv => `${inv.invoiceNumber}\t${asCurrency(inv.invoicedAmount, inv.currency)}\t${asDate(inv.invoiceDueDate)}`)
            .join(',\r\n');
        navigator.clipboard.writeText(`Invoice Number\t Invoiced Amount\t Due Date\t\r\n${invoiceRows}\r\n\r\n`);

        a.href = `mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();

        state.selected.forEach(inv => {
            state.invoices.get(inv.id).set({ ...inv, emailSentOn: new Date() });
        });

        execSetEmailSentFlag(state.selected.map(inv => inv.id), true);
    };

    const $showCreateRA = useLinked(false);
    const createRA = () => {
        $showCreateRA.true();
    }

    const raCreated = () => {
        $doIO.set(!$doIO.value);
        state.selected.reset();
    }

    return <>
        <PageHeaderTile
            toolbar={
                <Toolbar>
                    <Flex grow />
                    {status !== 'reconciled' && Can(UserEntitlementCatalogs.BankEntryWrite) && (
                        <PrimaryButton 
                            disabled={!state.selected.length || state.selected.some(inv => inv.unreconciledAmount === 0)} 
                            onClick={createRA}
                            > Create RA
                        </PrimaryButton>
                    )}
                    {status === 'new' || status === 'promised' ?
                        <Button onClick={mailTo} disabled={!state.selected.length}>
                            Email
                        </Button> : undefined
                    }
                </Toolbar>
            }
            color={colors["green"]}
            title={<CustomerSupplier customer={customerDebth.customer} supplier={customerDebth.supplier} />}
        >
            <Flex column>
                <RowH1>{asCurrency(customerDebth.outstandingAmount || customerDebth.invoicedAmount, customerDebth.currency )}</RowH1>
                <Flex row wrap>
                    <PageTileField label="Invoiced Amount">
                        {asCurrency(customerDebth.invoicedAmount, customerDebth.currency)}
                    </PageTileField>
                    <PageTileField label="Invoice Count">
                        {customerDebth.count}
                    </PageTileField>
                    <PageTileField label="Past due Amount">
                        {asCurrency(customerDebth.overdueAmount, customerDebth.currency)}
                    </PageTileField>
                    <PageTileField label="Past Due Count">
                        {customerDebth.overdueCount}
                    </PageTileField>
                    <PageTileField label="Promised Amount">
                        {asCurrency(customerDebth.promisedAmount, customerDebth.currency)}
                    </PageTileField>
                    <PageTileField label="Disputed Amount">
                        {asCurrency(customerDebth.disputedAmount, customerDebth.currency)}
                    </PageTileField>
                </Flex>
            </Flex>
        </PageHeaderTile>

        {isReady ?
            <CollapsableSection title={`Invoices`}>
                <Flex row>
                    <FormCheckbox
                        $checked={
                            Linked.value(
                                sortedAndFiltered.length ? sortedAndFiltered.length === state.selected.length : false,
                                x => x ? state.selected.set(sortedAndFiltered) : state.selected.reset()
                            )
                        }
                    />

                    <SearchInput
                        placeholder={`Filter by invoice number, invoice amount, invoice due date, outstanding amount ${status !== 'new' ? ', promise to pay date': ''}`}
                        $value={state.$.filter}
                    />

                    <SelectSortBy
                        $attr={state.sortBy.$.name}
                        $asc={state.sortBy.$.isAsc}
                        options={status === 'new' ? sortInvoicesOptions : {...sortInvoicesOptions, promiseToPayDate: 'Promise To Pay Date'}}
                    />
                </Flex>

                <TilesList
                    items={sortedAndFiltered}
                    itemSize={42}
                    height={600}
                    renderer={InvoiceTile(state.selected)}
                    selected={undefined /* selected */}
                />

                {$showCreateRA.value ? <CreateRADialog $isOpen={$showCreateRA} companies={companies} invoices={state.selected} onSuccess={raCreated} withAppLink /> : undefined}
            </CollapsableSection>
            :
            <Loading />
        }
    </>
}

const sortInvoicesOptions = {
    invoiceNumber: 'Invoice Number',
    invoiceDueDate: 'Invoice Due Date',
    invoicedAmount: 'Invoiced Amount',
    unreconciledAmount: 'Outstanding Amount',
};

export const SendToButton = ({ onClick }) =>
    <IconButton aria-label="Send To" size="small" onClick={onClick}>
        <img src="/assets/images/send-to-dark.png" alt="Send To" />
    </IconButton>

export const UnmatchConfirmationDialog = ({ $isOpen, onClick }) => {

    return (
        <PopupIODialog
            title="Are you sure you want to unmatch?"
            $isOpen={$isOpen}
            actionLabel="Yes"
            success="Unmatched"
            doIO={onClick}
            hideTitleOnSuccess
        />
    );
};

export const SmallBox = div({
    padding: 8,
    boxShadow: '1px 0 2px 0 rgb(0 0 0 / 4%)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: '#f9f9f9',
    alignItems: 'center',
    margin: 4
})

export const SmallInvoiceTile = ({ invoice, paymentId }) =>
    <SmallBox>
        <PaymentInfoIcon />
        <PopoverValue
            label={<RowH2Bold>{invoice.invoiceNumber}</RowH2Bold>}
            anchorAlign="left"
            transformAlign="left"
        >
            <InvoiceDetails invoice={invoice} paymentId={paymentId} />
        </PopoverValue>

        <FlexListField>{asCurrency(invoice.displayAmount, invoice.currency)}</FlexListField>
        <FlexListField>{asDate(invoice.estimatedPayDate)}</FlexListField>
    </SmallBox>


export const FlexListField = styled('div')({
    paddingLeft: 8,
    paddingRight: 8,
    fontSize: 12,
    color: '#A0A0A0'
})








